import { useLayoutEffect, useState } from 'react';

/**
 * React Flow measures its nodes and canvas with ResizeObserver and updates its store inside the
 * observer callback. That store update re-renders synchronously, so nodes get resized or newly
 * observed during the browser's resize-observation step, which the browser reports as
 * "ResizeObserver loop completed with undelivered notifications" (a crash in the CRA dev overlay).
 * Running the callbacks on the next animation frame instead breaks that loop.
 *
 * window.ResizeObserver is replaced only while a Field Mapper is mounted.
 */
let NativeResizeObserver = null;
let mountedCount = 0;

// Plain constructor (not a class) so the production build's older browser targets can't break it.
// Returning the native instance keeps the real ResizeObserver API.
function FrameDeferredResizeObserver(callback) {
  return new NativeResizeObserver((entries, observer) => {
    window.requestAnimationFrame(() => {
      // Skip elements removed from the page while waiting for the frame
      const connected = entries.filter((entry) => entry.target.isConnected);
      if (connected.length > 0) {
        callback(connected, observer);
      }
    });
  });
}

const install = () => {
  if (typeof window === 'undefined' || !window.ResizeObserver || window.ResizeObserver === FrameDeferredResizeObserver) {
    return;
  }
  NativeResizeObserver = window.ResizeObserver;
  FrameDeferredResizeObserver.prototype = NativeResizeObserver.prototype;
  window.ResizeObserver = FrameDeferredResizeObserver;
};

const uninstall = () => {
  if (mountedCount === 0 && window.ResizeObserver === FrameDeferredResizeObserver) {
    window.ResizeObserver = NativeResizeObserver;
  }
};

export const useFrameDeferredResizeObserver = () => {
  // Install during render: React Flow creates its node observer while rendering, before effects run
  useState(install);

  useLayoutEffect(() => {
    mountedCount += 1;
    install();
    return () => {
      mountedCount -= 1;
      // Deferred so a remount in the same commit (entity switch, StrictMode) keeps the patch
      setTimeout(uninstall, 0);
    };
  }, []);
};
